/** Shared building blocks for the report pages (P52): month chips, the
 * per-exit-style summary grid, and the per-trade comparison table. Used
 * by the /reports hub (summary per strategy) and each strategy's own
 * /reports/:strategyId detail page. */

import { useEffect, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { reportsApi } from "@/lib/reportsApi";
import type { ReportExecutionMode, TradeComparison } from "@/lib/reportsApi";
import { computeStyleStats, monthLabel, tradingDays, type StyleStats } from "./exitStats";
import { dayLabel, rupees, signedRupees, tone } from "./reportFormat";
import { CapitalCard } from "./CapitalCard";

/** Opening capital shown on the Capital card — same convention as the
 * operator's reference dashboard (₹2,00,000). Display-only. */

/** Live/Paper switch for the report pages — swaps every number and table
 * on the page between the two universes (they never mix). */
export function ReportModeToggle({
  value,
  onChange,
}: Readonly<{
  value: ReportExecutionMode;
  onChange: (m: ReportExecutionMode) => void;
}>): ReactElement {
  return (
    <div className="flex rounded-ctl border border-line p-0.5 text-xs" role="group">
      {(["LIVE", "PAPER"] as const).map((k) => {
        const active = k === value;
        const activeCls =
          k === "PAPER" ? "bg-warning-bg text-warning" : "bg-success-bg text-success";
        return (
          <button
            key={k}
            type="button"
            aria-pressed={active}
            className={
              active
                ? `rounded-ctl px-3 py-1 font-medium ${activeCls}`
                : "rounded-ctl px-3 py-1 text-ink-secondary hover:bg-surface-2"
            }
            onClick={() => {
              onChange(k);
            }}
          >
            <i className={`ti ${k === "PAPER" ? "ti-flask" : "ti-rocket"} mr-1 text-xs`} />
            {k === "LIVE" ? "Live" : "Paper"}
          </button>
        );
      })}
    </div>
  );
}

/** Live/Paper pill for one report row — instant reassurance about whether
 * real money moved on that trade. */
export function RowModePill({ mode }: Readonly<{ mode: ReportExecutionMode }>): ReactElement {
  if (mode === "PAPER") {
    return (
      <span className="pill bg-warning-bg text-warning">
        <i className="ti ti-flask text-xs" />
        Paper
      </span>
    );
  }
  return (
    <span className="pill bg-success-bg text-success">
      <i className="ti ti-rocket text-xs" />
      Live
    </span>
  );
}

export function MonthChips({
  active,
  onPick,
  strategyId,
  mode = "LIVE",
}: Readonly<{
  active: string;
  onPick: (key: string) => void;
  strategyId?: string | undefined;
  mode?: ReportExecutionMode;
}>): ReactElement {
  // Only months that actually contain trades become tabs — empty months
  // are noise (operator feedback). Falls back to the current month while
  // loading / when there is no data at all.
  const monthsQuery = useQuery({
    queryKey: ["report-months", strategyId ?? "all", mode],
    queryFn: () => reportsApi.months(strategyId, mode),
  });
  const keys =
    monthsQuery.data !== undefined && monthsQuery.data.months.length > 0
      ? monthsQuery.data.months
      : [active];
  useEffect(() => {
    if (monthsQuery.data !== undefined && monthsQuery.data.months.length > 0) {
      const months = monthsQuery.data.months;
      if (!months.includes(active) && months[0] !== undefined) onPick(months[0]);
    }
  }, [monthsQuery.data, active, onPick]);
  return (
    <div className="mb-5 flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium uppercase tracking-wider text-ink-secondary">Month</span>
      {keys.map((key) => (
        <button
          key={key}
          type="button"
          className={`rounded-ctl border px-3 py-1 text-xs font-medium ${
            key === active
              ? "border-accent-border bg-accent-bg text-accent"
              : "border-line bg-surface-1 text-ink-secondary hover:bg-surface-2"
          }`}
          onClick={() => {
            onPick(key);
          }}
        >
          {monthLabel(key)}
        </button>
      ))}
    </div>
  );
}

export function SummaryGrid({
  trades,
  strategyId,
  compact = false,
}: Readonly<{ trades: TradeComparison[]; strategyId?: string; compact?: boolean }>): ReactElement {
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {/* The ACCOUNT card when we know whose account it is; the old
          trades-only card otherwise (the hub renders per strategy, so in
          practice it always knows). */}
      {strategyId !== undefined ? (
        <CapitalCard strategyId={strategyId} compact={compact} />
      ) : (
        <TradeCountCard trades={trades} />
      )}
      <StyleCard
        label="Realised"
        hint="What the bot actually did"
        stats={computeStyleStats(trades)}
      />
    </div>
  );
}

function TradeCountCard({ trades }: Readonly<{ trades: TradeComparison[] }>): ReactElement {
  return (
    <div className="stat-card">
      <div className="text-xs uppercase tracking-wider text-ink-secondary">This selection</div>
      <div className="mt-1 text-2xl font-medium">{String(trades.length)}</div>
      <div className="text-xs text-ink-muted">Trades in view</div>
      <div className="mt-3 border-t border-line pt-2">
        <StatRow label="Trades" value={String(trades.length)} />
        <StatRow label="Days" value={String(tradingDays(trades))} />
      </div>
    </div>
  );
}

function StyleCard({
  label,
  hint,
  stats,
}: Readonly<{ label: string; hint: string; stats: StyleStats }>): ReactElement {
  return (
    <div className="stat-card">
      <div className="text-xs uppercase tracking-wider text-ink-secondary">{label}</div>
      <div className="mb-2 text-[11px] text-ink-muted">{hint}</div>
      <StatRow label="Gross P&L" value={signedRupees(stats.grossPnl)} tone={tone(stats.grossPnl)} />
      <StatRow
        label="Charges"
        value={stats.charges > 0 ? `-${rupees(stats.charges)}` : "—"}
        tone={stats.charges > 0 ? "text-warning" : undefined}
        sub={stats.charges > 0 ? undefined : "none entered"}
      />
      <StatRow label="Net P&L" value={signedRupees(stats.netPnl)} tone={tone(stats.netPnl)} />
      <StatRow
        label="Win Rate"
        value={stats.winRate !== null ? `${String(stats.winRate)}%` : "—"}
        sub={`${String(stats.wins)} of ${String(stats.closed)}`}
      />
      <StatRow
        label="Max Drawdown"
        value={stats.maxDrawdown < 0 ? signedRupees(stats.maxDrawdown) : "—"}
        tone={stats.maxDrawdown < 0 ? "text-warning" : undefined}
        sub={dayLabel(stats.maxDrawdownDate)}
      />
      <StatRow
        label="Max Run-Up"
        value={stats.maxRunUp > 0 ? signedRupees(stats.maxRunUp) : "—"}
        tone={stats.maxRunUp > 0 ? "text-success" : undefined}
        sub={dayLabel(stats.maxRunUpDate)}
      />
    </div>
  );
}

function StatRow({
  label,
  value,
  tone: valueTone,
  sub,
}: Readonly<{
  label: string;
  value: string;
  tone?: string | undefined;
  sub?: string | undefined;
}>): ReactElement {
  return (
    <div className="flex items-baseline justify-between border-b border-line py-1 text-sm last:border-0">
      <span className="text-xs text-ink-secondary">{label}</span>
      <span className="text-right">
        <span className={`font-medium ${valueTone ?? ""}`}>{value}</span>
        {sub !== undefined && sub !== "" && (
          <span className="block text-[10px] text-ink-muted">{sub}</span>
        )}
      </span>
    </div>
  );
}
